import React, { useState, useEffect, useRef } from 'react';
import { useStateCallback } from '@utils/stateCallback';
import { makeStyles } from '@material-ui/core/styles';
import { getHRRequestDocumentsList, saveHRRequest, deleteHRRequest } from '@actions/HRRequest';
import * as StatusCode from '@scripts/StatusCode';
import i18next from 'i18next';
import PropTypes from 'prop-types'
import styles from '@styles/request.module.css';
import { isIOS } from '@utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faTrash } from '@fortawesome/free-solid-svg-icons';
import List from '@material-ui/core/List';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import punchStyles from '@styles/punch.module.css';
import CircularProgress from '@material-ui/core/CircularProgress';

const useStyles = makeStyles({
    table: {
        '& td': {
            fontSize: 16,
            '@media (max-width: 600px)': {
                fontSize: 11,
            }
        },
        '& td:first-child': {
            width: '25%',
        },

        '& td:last-child': {
            width: '75%',
        },
    },
    head: {
        lineHeight: '0.5'
    },
});

export default function HRRequestDetailDocumentDeposit(props) {

    const classes = useStyles();
    const [isPostCompleted, setIsPostCompleted] = useStateCallback(true);
    const [files, setFiles] = useState([]);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [openViewer, setOpenViewer] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const {data, dataRequest } = props;
    const [isiOS, setIsiOS] = useState(isIOS());
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [editIndex, setEditIndex] = useState(null);
    const [editFile, setEditFile] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        resizeForm();
        if (data?.hrRequest?.hrRequestId) {
            fetchFiles();
        }
    }, [])

    const resizeForm = () => {
        setTimeout(function () {
            const height = window.innerHeight;
            const titleBar = document.querySelector('#appBarTitle').clientHeight;
            const offSet = isiOS ? 183 : 173;

            const customHeight = height - titleBar - offSet;

            const element = document.querySelector('#divDetailDocumentDeposit');

            if (element !== undefined && element !== null) {
                element.style.height = `${customHeight}px`;
            }
        }, 100);
    }

    const fetchFiles = async () => {
        return new Promise(async (resolve) => {
            setLoadingFiles(true);
            const result = await getHRRequestDocumentsList(data?.hrRequest?.hrRequestId);

            if (result !== undefined) {
                if (result != null && result.status == StatusCode.STATUS_OK) {
                    const parsedResult = JSON.parse(result.result);

                    if (parsedResult.documentList != null && parsedResult.documentList.length > 0 ) {
                        setFiles(parsedResult.documentList);
                    }
                }
                else {
                    showMessageError(i18next.t('error:generic-error'));
                }
            }

            setLoadingFiles(false);
        })
    };

    const handleClick = (file) => {
        const extension = file.documentName
            .split('.')
            .pop()
            .toLowerCase();

        // Previewable types
        if (['jpg', 'jpeg', 'png', 'gif', 'pdf'].includes(extension)) {

            setSelectedFile(file);
            setOpenViewer(true);

            return;
        }

        // Everything else downloads
        const src = getDocumentSource(file);
        downloadDocument(src, file.documentName);
    };

    const handleConfirmDelete = () => {
        setOpenDeleteDialog(false);
        handleDeleteRequest();
    };

    const downloadDocument = (file, filename) => {

        const binary = atob(file);
        const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
        const blob = new Blob([bytes]);

        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };

    const renderDocumentViewer = () => {

        if (!selectedFile) {
            return null;
        }

        const extension = getFileExtension(selectedFile.documentName);

        // Use whichever field contains your file URL/base64
        const fileSource = getDocumentSource(selectedFile);

        if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
            return (
                <img
                    src={fileSource}
                    alt={selectedFile.documentName}
                    style={{
                        width: '100%',
                        maxHeight: '80vh',
                        objectFit: 'contain'
                    }}
                />
            );
        }

        if (extension === 'pdf') {
            return (
                <iframe title="PDF Viewer" src={fileSource} width="100%" height="600px" style={{ border: 'none' }} />
            );
        }

        return (
            <div>
                {i18next.t('hrRequest:form.unsupportedFileType')}
            </div>
        );
    };

    const getFileExtension = (filename) => {
        if (!filename) {
            return '';
        }
        return filename.split('.').pop().toLowerCase();
    };

    const getDocumentSource = (file) => {

        const extension = getFileExtension(file.documentName);

        if (extension === 'pdf') {
            return `data:application/pdf;base64,${file.document}`;
        }

        if (extension === 'png') {
            return `data:image/png;base64,${file.document}`;
        }

        if (extension === 'jpg' || extension === 'jpeg') {
            return `data:image/jpeg;base64,${file.document}`;
        }

        return file.document;
    };

    const handleFileSelected = (event) => {
        const selected = event.target.files[0];

        if (!selected) {
            return;
        }

        const reader = new FileReader();

        reader.onload = () => {

            const base64 = reader.result.split(',')[1];

            const tempFileKey = `file_${Date.now()}`;

            const newFile = {
                hrRequestDocumentId: 0,
                documentName: selected.name,
                comment: '',
                document: base64,

                // IMPORTANT
                fileObject: selected,
                tempFileKey: tempFileKey,

                // backend fields
                gdDocumentIDEncrypt: '',
                documentDataBase64: base64
            };

            setEditFile(newFile);
            setEditIndex(null);
            setOpenEditDialog(true);
        };

        reader.readAsDataURL(selected);
        event.target.value = null;
    };

    const handleDeleteFile = (index) => {
        const updatedFiles = [...files];
        updatedFiles.splice(index, 1);
        setFiles(updatedFiles);
    };

    const handleEditFile = (file, index) => {
        setEditFile({ ...file });
        setEditIndex(index);
        setOpenEditDialog(true);
    };

    const handleSaveFile = () => {

        const updatedFiles = [...files];

        if (editIndex === null) {
            updatedFiles.push(editFile);
        }
        else {
            updatedFiles[editIndex] = editFile;
        }

        setFiles(updatedFiles);
        setOpenEditDialog(false);
        setEditFile(null);
        setEditIndex(null);
    };

    const handleAddFile = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleSaveRequest = () => {
        return new Promise(async (resolve) => {
            const result = await saveHRRequest(data, files);

            if (result !== undefined) {
                if (result != null && result.status === StatusCode.STATUS_OK) {
                    setIsPostCompleted(true);
                    props.onReload?.();
                    props.onClose();
                }
                else {
                    showMessageError(i18next.t('error:generic-error'));
                    setIsPostCompleted(true);

                }
            }
        })
    };

    const handleDeleteRequest = () => {
        return new Promise(async (resolve) => {
            const result = await deleteHRRequest(data.hrRequest.hrRequestId);

            if (result !== undefined) {
                if (result != null && result.status === StatusCode.STATUS_OK) {
                    setIsPostCompleted(true);
                    props.onReload?.();
                    props.onClose();
                }
                else {
                    showMessageError(i18next.t('error:generic-error'));
                    setIsPostCompleted(true);
                }
            }
        })
    };

    return (
        <React.Fragment>
            <div id="divDetailDocumentDeposit" className={styles.detailRequest} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '16px' }}>

                        <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }} >
                            {i18next.t('hrRequest:form.files')}
                        </div>

                        <div style={{ marginBottom: '16px' }} >
                            <Button className={punchStyles.punchButton} onClick={handleAddFile} fullWidth >
                                {i18next.t('hrRequest:buttons.addFile')}
                            </Button>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>

                        {loadingFiles && (<div>{i18next.t('hrRequest:form.loading')}</div> )}

                        {!loadingFiles && files.length === 0 && (<div>{i18next.t('hrRequest:form.noFiles')}</div> )}

                        {!loadingFiles && files.length > 0 && (
                            <List>
                                {files.map((file, index) => (

                                    <div key={index}
                                         style={{ display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            marginBottom: '10px',
                                            background: '#fff'
                                        }}
                                        onMouseEnter={(e) =>
                                            e.currentTarget.style.background = '#f8f8f8'
                                        }
                                        onMouseLeave={(e) =>
                                            e.currentTarget.style.background = '#fff'
                                        }
                                    >

                                    <div style={{flex: 1, minWidth: 0, cursor: 'pointer', overflow: 'hidden' }} onClick={() => handleEditFile(file, index) } >

                                            <div style={{fontSize: '15px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} >
                                            {file.documentName}
                                        </div>

                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {file.comment || 'No comment'}
                                        </div>

                                    </div>

                                        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, marginLeft: '12px' }} >

                                        <IconButton onClick={() => handleClick(file) } >
                                            <FontAwesomeIcon icon={faEye} />
                                        </IconButton>

                                        <IconButton onClick={() => handleDeleteFile(index) } >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </IconButton>

                                        </div>
                                    </div>
                                ))}
                            </List>
                        )}
                        </div>
                    </div>
                    <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelected} /> 
                   
                    <div style={{ borderTop: '1px solid #ddd', padding: '16px', display: 'flex', justifyContent: 'center', gap: '16px', background: '#fff', flexShrink: 0 }} >
                        <Button className={punchStyles.punchButton} style={{ minWidth: '120px' }} onClick={handleSaveRequest} >
                        {!isPostCompleted ? <CircularProgress size={20} /> : i18next.t('hrRequest:buttons.save')}
                        </Button>
                    {(data?.hrRequest?.hrRequestId ?? 0) > 0 &&
                        (<Button variant="contained"
                            style={{ backgroundColor: '#b51200', color: '#fff', minWidth: '120px' }}
                            onClick={() => setOpenDeleteDialog(true)} >
                            {!isPostCompleted
                                ? <CircularProgress size={20} />
                                : i18next.t('hrRequest:buttons.delete')}
                        </Button>)}
                    </div>

                    <Dialog open={openViewer} onClose={() => setOpenViewer(false)} maxWidth="lg" fullWidth style={{ zIndex: 11003 }} >
                        <DialogTitle>
                            {selectedFile?.documentName}
                        </DialogTitle>

                        <DialogContent>
                            {renderDocumentViewer()}
                        </DialogContent>
                    </Dialog>

                    <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth style={{ zIndex: 11004 }}>

                        <DialogTitle>
                            {i18next.t('hrRequest:form.editRequest')}
                        </DialogTitle>

                        <DialogContent>
                            
                            <TextField label={i18next.t('hrRequest:form.fileName')} value={editFile?.documentName || ''}
                                onChange={(e) =>
                                    setEditFile({
                                        ...editFile,
                                        documentName: e.target.value
                                    })
                                }
                                fullWidth
                                margin="normal"
                            />

                            <TextField label={i18next.t('hrRequest:form.comment')} value={editFile?.comment || ''}
                                onChange={(e) =>
                                    setEditFile({
                                        ...editFile,
                                        comment: e.target.value
                                    })
                                }
                                fullWidth
                                multiline
                                minRows={3}
                                margin="normal"
                            />

                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }} >
                                <Button className={punchStyles.punchButton} onClick={handleSaveFile} >
                                     {i18next.t('hrRequest:buttons.save')}
                                </Button>
                                <Button onClick={() => setOpenEditDialog(false)} variant="contained" style={{ backgroundColor: '#b51200', color: '#fff', minWidth: '120px' }}>
                                    {i18next.t('hrRequest:buttons.cancel')}
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>

                    <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="sm" fullWidth style={{ zIndex: 11005 }} >
                        <DialogTitle>
                            {i18next.t('hrRequest:form.confirmDeleteTitle')}
                        </DialogTitle>

                        <DialogContent>
                            <div style={{ marginBottom: '20px' }}>
                                {i18next.t('hrRequest:form.confirmDeleteMessage')}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <Button onClick={() => setOpenDeleteDialog(false)} color="primary" variant="contained" >
                                    {i18next.t('hrRequest:buttons.cancel')}
                                </Button>

                                <Button onClick={handleConfirmDelete} variant="contained" style={{ backgroundColor: '#b51200', color: '#fff', minWidth: '120px' }} >
                                    {i18next.t('hrRequest:buttons.delete')}
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
            </div>
        </React.Fragment>
    );
}

HRRequestDetailDocumentDeposit.propTypes = {
    data: PropTypes.object.isRequired,
    dataRequest: PropTypes.object.isRequired
}